import React, { useState, useRef, useEffect } from 'react';
typeof window !== 'undefined' && !window.__DS_WEB_VERSION__ && (window.__DS_WEB_VERSION__ = '2.10.0');
import { X } from '@olxbr/ds-icons';
import { isEmpty, isNotEmpty, generateHash } from '../../helpers';
import { Flex } from '../Flex';
import { Spinner } from '../Spinner';
import { SuperiorContent } from '../TextInput/SuperiorContent';
import { InferiorContent } from '../TextInput/InferiorContent';
import { InputChipsDefaultProps } from './props';
import * as S from './styles';
const INPUT_CHIPS_ACCESSIBILITY_LABEL_ERROR = '[InputChips - Accessibility Error] Um elemento de input deve ter um label associado. Informe pelo menos um dos atributos "label" | "aria-label" com valor válido.';
const INPUT_CHIPS_ACCESSIBILITY_LOADING_ERROR = '[InputChips - Accessibility Error] Quando o state for "loading" é obrigatório informar um "stateMessage" para indicar para os leitores de tela a mensagem de carregamento.';
const INPUT_CHIPS_LABEL_WITHOUT_ID_ERROR = '[InputChips - Error] Para utilização da label no componente é obrigatório a utilização de um id válido.';
const runValidations = _ref => {
  let {
    'aria-label': ariaLabel,
    id,
    label,
    state,
    stateMessage
  } = _ref;
  if (isEmpty(label) && isEmpty(ariaLabel)) throw new Error(INPUT_CHIPS_ACCESSIBILITY_LABEL_ERROR);
  if (isNotEmpty(label) && isEmpty(id)) throw new Error(INPUT_CHIPS_LABEL_WITHOUT_ID_ERROR);
  if (state === 'loading' && isEmpty(stateMessage)) throw new Error(INPUT_CHIPS_ACCESSIBILITY_LOADING_ERROR);
};
/**
 * Componente InputChips do Design System OLX
 */
export const InputChips = props => {
  const {
    id,
    label,
    placeholder,
    'aria-label': ariaLabel,
    value = InputChipsDefaultProps.value,
    link,
    disabled,
    disableBackspaceChipRemove = InputChipsDefaultProps.disableBackspaceChipRemove,
    separatorCode = InputChipsDefaultProps.separatorCode,
    state,
    stateMessage,
    caption,
    onChange,
    onChipRemove
  } = props;
  runValidations(props);
  const elementId = id || generateHash();
  const [errorId, setErrorId] = useState('');
  useEffect(() => {
    setErrorId("error-".concat(Math.floor(Math.random() * 101)));
  }, []);
  const inputRef = useRef(null);
  const [chips, setChips] = useState(value);
  const focusOnInput = () => {
    if (inputRef === null || inputRef === void 0 ? void 0 : inputRef.current) {
      inputRef.current.focus();
    }
  };
  return React.createElement(Flex, {
    "data-ds-component": "DS-InputChips",
    flexDirection: "column"
  }, React.createElement(SuperiorContent, {
    id: elementId,
    label: label,
    link: link,
    size: "medium"
  }), React.createElement(S.InputChipsWrapper, {
    id: "".concat(elementId, "-wrapper"),
    disabled: disabled,
    hasContent: (chips === null || chips === void 0 ? void 0 : chips.length) > 0,
    state: state,
    onClick: focusOnInput
  }, chips.map((chip, index) => React.createElement(S.Chip, {
    key: "".concat(elementId, "-chip-").concat(index),
    id: "".concat(elementId, "-chip-").concat(index),
    disabled: disabled
  }, chip, React.createElement(S.ChipRemoveButton, {
    "data-ds-component": "DS-InputChips-Button",
    id: "".concat(elementId, "-chip-remove-button-").concat(index),
    "aria-label": "Remover ".concat(chip),
    disabled: disabled,
    onClick: e => {
      e.stopPropagation();
      const currentChips = chips.filter(currentChip => currentChip !== chip) || [];
      setChips(currentChips);
      onChange && onChange(currentChips);
      onChipRemove && onChipRemove(chip);
    }
  }, React.createElement(X, {
    size: "16px"
  })))), React.createElement(S.Input, {
    "data-ds-component": "DS-InputChips-Input",
    ref: inputRef,
    id: "".concat(elementId, "-input"),
    type: "text",
    placeholder: placeholder,
    "aria-label": ariaLabel,
    "aria-busy": state === 'loading',
    "aria-invalid": state === 'error',
    disabled: disabled,
    autoComplete: "off",
    onKeyDown: e => {
      e.stopPropagation();
      const inputValue = e.currentTarget.value;
      if (!inputValue && chips.length && e.key === 'Backspace') {
        e.currentTarget.value = disableBackspaceChipRemove ? "".concat(chips.slice(-1), " ") : '';
        const currentChips = [...chips.slice(0, -1)] || [];
        setChips(currentChips);
        onChange && onChange(currentChips);
      }
      const text = inputValue === null || inputValue === void 0 ? void 0 : inputValue.trim();
      if (text && text.length && [e === null || e === void 0 ? void 0 : e.code, e === null || e === void 0 ? void 0 : e.key].some(code => code === separatorCode)) {
        e.preventDefault();
        if (chips.includes(text)) return;
        const currentChips = [...chips, text] || [];
        setChips(currentChips);
        onChange && onChange(currentChips);
        e.currentTarget.value = '';
      }
    }
  }), state === 'loading' && React.createElement(S.IconWrapper, null, React.createElement(Spinner, {
    "aria-label": stateMessage !== null && stateMessage !== void 0 ? stateMessage : '',
    size: "extra-small"
  }))), React.createElement(InferiorContent, {
    errorId: errorId,
    stateMessage: stateMessage,
    state: state,
    disabled: disabled,
    caption: caption
  }));
};
InputChips.displayName = 'InputChips';